import { Head, Link, useForm } from '@inertiajs/react'
import { Plus, Upload } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { adminProductionArchives, adminProductions } from '@/routes'
import type { Production } from '@/types'

export default function ProductionsIndex({ productions }: { productions: Production[] }) {
  return (
    <div className="grid gap-6">
      <Head title="Productions" />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Productions</h1>
        <Button asChild>
          <Link href={adminProductions.new()}>
            <Plus /> New production
          </Link>
        </Button>
      </div>

      {productions.length === 0 ? (
        <p className="text-muted-foreground">No productions yet. Create one, then paste in the script.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {productions.map((p) => (
            <Link key={p.id} href={adminProductions.show(p.slug)}>
              <Card className="transition-colors hover:bg-muted/50">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-2">
                    {p.title}
                    {p.publishedVersion > 0 ? (
                      <Badge variant="secondary">v{p.publishedVersion}</Badge>
                    ) : (
                      <Badge variant="outline">Draft</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {p.sceneCount} scenes · {p.characterCount} characters
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      )}
      <ImportCard />
    </div>
  )
}

/** Load a .scripted file exported from another environment (script, voices, audio). */
function ImportCard() {
  const form = useForm<{ file: File | null; replace: boolean }>({ file: null, replace: false })
  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="text-base">Import a production</CardTitle>
        <CardDescription>
          A <code>.scripted</code> file from Export — script, voices and audio, no regeneration needed. Cast assignments
          aren’t included; when replacing, existing ones are kept.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            form.transform((data) => ({ file: data.file, replace: data.replace ? '1' : '0' }))
            form.submit(adminProductionArchives.create(), { forceFormData: true })
          }}
        >
          <Input type="file" accept=".scripted" onChange={(e) => form.setData('file', e.target.files?.[0] ?? null)} />
          {/* biome-ignore lint/a11y/noLabelWithoutControl: Checkbox renders the control inside the label */}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.data.replace} onCheckedChange={(on) => form.setData('replace', on === true)} />
            Replace the production if it already exists (its script and audio are swapped; publish again afterwards)
          </label>
          <Button
            type="submit"
            variant="outline"
            disabled={!form.data.file || form.processing}
            className="justify-self-start"
          >
            <Upload /> {form.processing ? 'Importing…' : 'Import'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
